/**
 * J3 — shared detector registry, short aliases, and parity helpers.
 *
 * Short IDs (E3, G1, …) map to R-* detector IDs used in code.
 */
export type AuditDetectorId =
  | 'R-3'
  | 'R-65'
  | 'R-71'
  | 'R-78'
  | 'R-66'
  | 'R-67'
  | 'R-62'
  | 'R-63'
  | 'R-64'
  | 'R-68'
  | 'R-69'
  | 'R-70'
  | 'R-72'
  | 'R-75'
  | 'R-77'
  | 'R-79'
  | 'R-8'
  | 'R-9'
  | 'R-15'
  | 'R-131-EPISTEMIC-GROUNDING-B'
  | 'R-132-ANTI-AI-VOICE'
  | 'tier-audit';

export type ShortDetectorId =
  | 'E3'
  | 'I2'
  | 'E2'
  | 'G1'
  | 'G2'
  | 'H1'
  | 'H2'
  | 'H3'
  | 'I1'
  | 'I3'
  | 'J1'
  | 'J2'
  | 'E1'
  | 'Q1'
  | 'K1'
  | 'L1'
  | 'M1'
  | 'N1';

export type DetectorRunStatus = 'ok' | 'failed' | 'skipped';

/** Full-framework pipeline order (13 core detectors; tier-audit is supplemental). */
export const FULL_AUDIT_SHORT_ORDER: readonly ShortDetectorId[] = [
  'E3',
  'I2',
  'E2',
  'G1',
  'G2',
  'H1',
  'H2',
  'H3',
  'I1',
  'I3',
  'J1',
  'J2',
  'E1',
  'Q1',
  'K1',
  'L1',
  'M1',
  'N1',
] as const;

/** Post-generation gate order (subset of full audit). */
export const POST_GEN_SHORT_ORDER: readonly ShortDetectorId[] = [
  'E2',
  'G2',
  'G1',
  'I1',
  'H1',
  'H2',
  'H3',
  'I3',
  'J1',
  'J2',
  'M1',
  'N1',
] as const;

export const SHORT_TO_AUDIT_IDS: Record<ShortDetectorId, readonly AuditDetectorId[]> = {
  E3: ['R-65'],
  I2: ['R-71'],
  E2: ['R-78'],
  G1: ['R-66'],
  G2: ['R-67'],
  H1: ['R-62', 'R-63', 'R-64'],
  H2: ['R-68'],
  H3: ['R-69'],
  I1: ['R-70'],
  I3: ['R-72'],
  J1: ['R-75'],
  J2: ['R-77'],
  E1: ['R-79'],
  Q1: ['R-8'],
  K1: ['R-9'],
  L1: ['R-15'],
  M1: ['R-131-EPISTEMIC-GROUNDING-B'],
  N1: ['R-132-ANTI-AI-VOICE'],
};

export const PARITY_ALLOWLIST = {
  postGenOnly: [] as ShortDetectorId[],
  userAuditOnly: ['E3', 'I2', 'E1', 'Q1', 'K1', 'L1'] as ShortDetectorId[],
};

export function expandShortIds(shortIds: readonly ShortDetectorId[]): AuditDetectorId[] {
  return shortIds.flatMap((s) => [...SHORT_TO_AUDIT_IDS[s]]);
}

export function expandShortOrder(order: readonly ShortDetectorId[]): AuditDetectorId[] {
  return expandShortIds(order);
}

export function shortFromAuditId(id: AuditDetectorId): ShortDetectorId | null {
  for (const [short, ids] of Object.entries(SHORT_TO_AUDIT_IDS) as Array<[ShortDetectorId, readonly AuditDetectorId[]]>) {
    if (ids.includes(id)) return short;
  }
  return null;
}

export function invokedDetectorIds(
  status: Partial<Record<AuditDetectorId, DetectorRunStatus>>,
): Set<AuditDetectorId> {
  const out = new Set<AuditDetectorId>();
  for (const [id, st] of Object.entries(status) as Array<[AuditDetectorId, DetectorRunStatus]>) {
    if (st === 'ok' || st === 'failed') out.add(id);
  }
  return out;
}

export interface DetectorSetDiff {
  missingInPostGen: AuditDetectorId[];
  missingInFull: AuditDetectorId[];
  allowlistedFullOnly: AuditDetectorId[];
}

export function diffDetectorSets(
  postGen: Set<AuditDetectorId>,
  full: Set<AuditDetectorId>,
  allowlist: typeof PARITY_ALLOWLIST = PARITY_ALLOWLIST,
): DetectorSetDiff {
  const allowedFullOnly = new Set(expandShortIds(allowlist.userAuditOnly));
  const allowedPostOnly = new Set(expandShortIds(allowlist.postGenOnly));

  const missingInPostGen: AuditDetectorId[] = [];
  for (const id of full) {
    if (!postGen.has(id) && !allowedFullOnly.has(id)) missingInPostGen.push(id);
  }

  const missingInFull: AuditDetectorId[] = [];
  for (const id of postGen) {
    if (!full.has(id) && !allowedPostOnly.has(id)) missingInFull.push(id);
  }

  const allowlistedFullOnly = [...full].filter((id) => allowedFullOnly.has(id) && !postGen.has(id));

  return { missingInPostGen, missingInFull, allowlistedFullOnly };
}

export function formatDetectorSetDiff(diff: DetectorSetDiff): string {
  const lines: string[] = [];
  if (diff.missingInPostGen.length > 0) {
    lines.push(`Missing in post-gen (not allowlisted): ${diff.missingInPostGen.join(', ')}`);
  }
  if (diff.missingInFull.length > 0) {
    lines.push(`Missing in full audit (not allowlisted): ${diff.missingInFull.join(', ')}`);
  }
  if (lines.length === 0) {
    return 'Detector sets match modulo PARITY_ALLOWLIST.';
  }
  return lines.join('\n');
}
